import type { ReactNode } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@ui/card.tsx';
import { Button } from '@ui/button.tsx';
import type { BoosterBoosterCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { BoosterStatusBadge } from './booster-status-badge.tsx';
import { canDeleteBooster } from './booster-utils.ts';

/** Ligne label / valeur dans le détail d'une carte booster. */
function BoosterDetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-border/60 py-3 last:border-0 sm:grid-cols-[minmax(180px,240px)_1fr] sm:items-start sm:gap-6">
      <p className="text-sm text-muted-foreground">{label}</p>
      <div className="text-sm">{value}</div>
    </div>
  );
}

/** Formate une date ISO API au format affiché dans l'admin (jj/mm/aaaa). */
function formatBoosterDate(date: string) {
  return format(new Date(date), 'dd/MM/yyyy', { locale: fr });
}

interface BoosterCardProps {
  booster: BoosterBoosterCollectionReadModel;
  showEdit?: boolean;
  showDelete?: boolean;
  onEdit?: (booster: BoosterBoosterCollectionReadModel) => void;
  onDelete?: (booster: BoosterBoosterCollectionReadModel) => void;
  isDeleting?: boolean;
}

/** Carte récapitulative d'un booster existant dans la liste. */
export function BoosterCard({
  booster,
  showEdit = false,
  showDelete = false,
  onEdit,
  onDelete,
  isDeleting = false
}: BoosterCardProps) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-6 py-4">
          <BoosterStatusBadge status={booster.status} />
          <div className="flex items-center gap-2">
            {showDelete && canDeleteBooster(booster) && (
              <Button
                type="button"
                variant="raw"
                className="h-fit rounded-md p-1.5 text-destructive/60 hover:text-destructive"
                onClick={() => onDelete?.(booster)}
                title={t('common.delete')}
                loading={isDeleting}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            {showEdit && (
              <Button
                type="button"
                variant="raw"
                className="h-fit gap-1.5 rounded-md p-1.5 text-primary hover:text-primary/80"
                title={t('common.edit')}
                onClick={() => onEdit?.(booster)}
              >
                <Pencil className="h-4 w-4" />
                <span className="text-sm font-medium">{t('common.edit')}</span>
              </Button>
            )}
          </div>
        </div>

        <div className="px-6 py-4">
          <h3 className="text-lg font-bold">{booster.title}</h3>
        </div>

        <div className="px-6 pb-6">
          <BoosterDetailRow
            label={t('boosters.create.description')}
            value={booster.description || t('boosters.list.description.empty')}
          />
          <BoosterDetailRow
            label={t('boosters.create.coefficient.label')}
            value={t('boosters.list.coefficient.summary')}
          />
          <BoosterDetailRow
            label={t('boosters.create.start.date')}
            value={formatBoosterDate(booster.startDate)}
          />
          <BoosterDetailRow
            label={t('boosters.create.end.date')}
            value={formatBoosterDate(booster.endDate)}
          />
          <BoosterDetailRow
            label={t('boosters.list.references.label')}
            value={t('boosters.list.references.count', { count: booster.referencesCount })}
          />
        </div>
      </CardContent>
    </Card>
  );
}
